import React, { useMemo, useState } from "react";
import FaqItem from "./FaqItem";

const FaqSection = () => {
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("All");
  const [openId, setOpenId] = useState(null);

  const faqs = useMemo(
    () => [
      {
        id: "eng-1",
        category: "Engagement",
        q: "What’s the fastest way to get started?",
        a: "Start with a short discovery call. We’ll clarify objectives, constraints, and recommend a path across training, consulting, or compliance support."
      },
      {
        id: "eng-2",
        category: "Engagement",
        q: "Do you support remote and onsite work?",
        a: "Yes. We can run fully remote, hybrid, or onsite sessions depending on the team, location, and timeline."
      },
      {
        id: "train-1",
        category: "Training",
        q: "Are trainings customized to our industry and processes?",
        a: "Yes. We tailor examples, exercises, and outcomes to your operating context while keeping the core methods consistent and repeatable."
      },
      {
        id: "train-2",
        category: "Training",
        q: "How do you ensure training actually sticks?",
        a: "We focus on adoption: structured practice, reinforcement loops, and simple routines that teams can repeat without relying on individual heroes."
      },
      {
        id: "comp-1",
        category: "Compliance",
        q: "Can you help us prepare for an audit?",
        a: "Yes. We help align documentation, routines, and governance to reduce risk and improve audit readiness without creating unnecessary overhead."
      },
      {
        id: "cons-1",
        category: "Consulting",
        q: "What outcomes should we expect from consulting support?",
        a: "Typical outcomes include clearer problem-solving routines, stronger accountability, reduced defects and rework, and improved operational performance."
      }
    ],
    []
  );

  const categories = useMemo(() => {
    const set = new Set(faqs.map((f) => f.category));
    return ["All", ...Array.from(set)];
  }, [faqs]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return faqs
      .filter((f) => (active === "All" ? true : f.category === active))
      .filter((f) => {
        if (!q) return true;
        return (
          f.q.toLowerCase().includes(q) ||
          f.a.toLowerCase().includes(q) ||
          f.category.toLowerCase().includes(q)
        );
      });
  }, [faqs, query, active]);

  return (
    <section className="relative w-full bg-white overflow-x-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-44 -right-44 h-[30rem] w-[30rem] rounded-full bg-[color:var(--brand-accent)]/10 blur-3xl" />
        <div className="absolute -bottom-52 -left-52 h-[36rem] w-[36rem] rounded-full bg-[color:var(--brand)]/10 blur-3xl" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-18 sm:py-20">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <h2 className="text-2xl sm:text-3xl font-semibold text-[color:var(--navy)]">
              Find answers fast
            </h2>
            <p className="mt-3 text-[color:var(--muted)]">
              Search questions or browse by topic.
            </p>
          </div>

          <div className="flex w-full flex-col gap-4 lg:w-auto">
            <div className="flex w-full sm:w-[360px] items-center gap-2 rounded-xl border border-[color:var(--border)] bg-[color:var(--surface)] px-4 py-3">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M10.5 18C14.1 18 17 15.1 17 11.5C17 7.9 14.1 5 10.5 5C6.9 5 4 7.9 4 11.5C4 15.1 6.9 18 10.5 18Z"
                  stroke="var(--brand)"
                  strokeWidth="2"
                />
                <path
                  d="M20 20L18 18"
                  stroke="var(--brand-accent)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search FAQs"
                className="w-full bg-transparent text-sm outline-none text-[color:var(--navy)] placeholder:text-[color:var(--muted)]"
                aria-label="Search FAQs"
              />
            </div>

            <div className="inline-flex flex-wrap items-center gap-2 rounded-2xl border border-[color:var(--border)] bg-white p-2">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => {
                    setActive(c);
                    setOpenId(null);
                  }}
                  className={`inline-flex items-center justify-center whitespace-nowrap rounded-xl px-4 py-2 text-xs font-semibold transition ${
                    active === c
                      ? "bg-[color:var(--brand)] text-white shadow-sm"
                      : "bg-[color:var(--surface)] text-[color:var(--navy)] hover:bg-white border border-[color:var(--border)]"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-10 grid gap-4">
          {filtered.map((f) => (
            <FaqItem
              key={f.id}
              id={f.id}
              category={f.category}
              question={f.q}
              answer={f.a}
              isOpen={openId === f.id}
              onToggle={() => setOpenId((prev) => (prev === f.id ? null : f.id))}
            />
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="mt-12 rounded-2xl border border-[color:var(--border)] bg-[color:var(--surface)] p-10 text-center">
            <div className="text-xl font-semibold text-[color:var(--navy)]">
              No matches found
            </div>
            <div className="mt-2 text-sm text-[color:var(--muted)]">
              Try a different search term or category.
            </div>
          </div>
        )}

        <div className="mt-14 rounded-2xl border border-[color:var(--border)] bg-[color:var(--navy)] p-10">
          <div className="text-2xl font-semibold text-white">
            Still have questions?
          </div>
          <p className="mt-3 text-white/80">
            Reach out and we’ll respond with the best next step.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <a
              href="/contact"
              className="inline-flex items-center justify-center rounded-xl px-7 py-3 text-sm font-semibold bg-[color:var(--brand-accent)] text-[color:var(--navy)] hover:brightness-95 transition shadow-lg"
            >
              Contact Us
            </a>
            <a
              href="/services"
              className="inline-flex items-center justify-center rounded-xl px-7 py-3 text-sm font-semibold bg-white/15 text-white hover:bg-white/25 transition"
            >
              View Services
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
